import { useCallback, useRef, useState } from "react";
import { supabase } from "../config";
import { DEFAULT_REEL_FRAME_THEME_ID } from "../constants/reelFrameThemes";

// Story-editing persistence shared by the Episode Metadata Editor and the
// Episode Editor (moved out of pages/EpisodeMetadataEditor.js, 2026-09-28,
// unchanged in behaviour). The page owns the data state; this hook owns
// debounced autosave + per-field save status and the few multi-row writes
// (story_reels upsert, writer find-or-create, "+ Add Story").

export const AUTOSAVE_DELAY_MS = 800;

export default function useStoryEditing({ episodeId, setStories, setPerformances, setWriters, setStoryReels, db = supabase }) {
  // db: the Episode Editor passes a client tagged with the signed-in editor
  // (x-kk-editor-* headers) so the change history records who saved what.
  const [saveStatus, setSaveStatus] = useState({});
  const saveTimers = useRef({});
  // story_id -> reel_id, once we've lazily created a story_reels row for a
  // story that didn't have one yet.
  const reelIdByStory = useRef({});

  const registerReels = useCallback((reelMap) => {
    Object.entries(reelMap).forEach(([sid, r]) => { reelIdByStory.current[sid] = r.reel_id; });
  }, []);

  // Update the existing story_reels row for this story, or create one on
  // first write — story_reels has no unique constraint on story_id (a
  // story can have multiple reel attempts over time), so "the row for this
  // editing session" is tracked client-side via reelIdByStory once created.
  const upsertStoryReel = useCallback(async (storyId, epId, patch) => {
    const existingId = reelIdByStory.current[storyId];
    if (existingId) {
      const { data, error } = await db.from("story_reels").update(patch).eq("reel_id", existingId).select().single();
      if (error) throw error;
      setStoryReels((prev) => ({ ...prev, [storyId]: data }));
      return data;
    }
    const { data, error } = await db.from("story_reels").insert({
      story_id: storyId, episode_id: epId,
      highlight_segments: [], frame_theme: DEFAULT_REEL_FRAME_THEME_ID,
      ...patch,
    }).select().single();
    if (error) throw error;
    reelIdByStory.current[storyId] = data.reel_id;
    setStoryReels((prev) => ({ ...prev, [storyId]: data }));
    return data;
  }, [setStoryReels, db]);

  // Find-or-create a writer by Telugu name, then point stories.writer_id at it.
  const lookupOrCreateWriter = useCallback(async (storyId, name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) {
      const { error } = await db.from("stories").update({ writer_id: null }).eq("story_id", storyId);
      if (error) throw error;
      return;
    }
    const { data: existing, error: findErr } = await db
      .from("writers").select("writer_id,name_telugu").eq("name_telugu", trimmed).maybeSingle();
    if (findErr) throw findErr;
    let writerId = existing?.writer_id;
    if (!writerId) {
      const { data: created, error: createErr } = await db
        .from("writers").insert({ name_telugu: trimmed }).select().single();
      if (createErr) throw createErr;
      writerId = created.writer_id;
    }
    setWriters((prev) => ({ ...prev, [writerId]: trimmed }));
    const { error: linkErr } = await db.from("stories").update({ writer_id: writerId }).eq("story_id", storyId);
    if (linkErr) throw linkErr;
  }, [setWriters, db]);

  // spec: {table, match:{col:val}, patch} for plain updates; or
  // {table:"story_reels", story_id, episode_id, patch}; or
  // {table:"writer_lookup", story_id, name}. immediateFn overrides spec.
  const onSaveField = useCallback((key, immediateFn, spec) => {
    return new Promise((resolve) => {
      setSaveStatus((s) => ({ ...s, [key]: "saving" }));
      clearTimeout(saveTimers.current[key]);
      saveTimers.current[key] = setTimeout(async () => {
        try {
          if (immediateFn) {
            await immediateFn();
          } else if (spec.table === "story_reels") {
            await upsertStoryReel(spec.story_id, spec.episode_id, spec.patch);
          } else if (spec.table === "writer_lookup") {
            await lookupOrCreateWriter(spec.story_id, spec.name);
          } else {
            let q = db.from(spec.table).update(spec.patch);
            Object.entries(spec.match).forEach(([col, val]) => { q = q.eq(col, val); });
            const { error } = await q;
            if (error) throw error;
          }
          setSaveStatus((s) => ({ ...s, [key]: "saved" }));
          setTimeout(() => setSaveStatus((s) => ({ ...s, [key]: null })), 1500);
          resolve({ ok: true });
        } catch (err) {
          console.error(`Save failed for ${key}:`, err);
          setSaveStatus((s) => ({ ...s, [key]: "error" }));
          resolve({ ok: false, error: err.message });
        }
      }, AUTOSAVE_DELAY_MS);
    });
  }, [upsertStoryReel, lookupOrCreateWriter, db]);

  // Generate Reel — applied immediately (not debounced): flips this
  // story's story_reels row to status='pending' with the current segments
  // + frame theme, for pipeline/reels/job_worker.py to pick up.
  const onGenerateReel = useCallback(async (storyId, epId, segments, frameTheme) => {
    if (!storyId) return { ok: false, error: "No story_id linked to this performance." };
    try {
      await upsertStoryReel(storyId, epId, {
        highlight_segments: segments, frame_theme: frameTheme, status: "pending", error_message: null,
      });
      return { ok: true };
    } catch (err) {
      console.error("Generate Reel failed:", err);
      return { ok: false, error: err.message };
    }
  }, [upsertStoryReel]);

  // "+ Add Story" — for a storyteller who told more than one story but only
  // has one auto-detected performance row. Creates a linked stories +
  // performances row together so every editable field works right away.
  const handleAddStory = useCallback(async (group) => {
    try {
      const { data: newStory, error: storyErr } = await db
        .from("stories").insert({ title_telugu: "" }).select().single();
      if (storyErr) throw storyErr;

      const { data: newPerf, error: perfErr } = await db
        .from("performances")
        .insert({
          episode_id: Number(episodeId),
          storyteller_id: group.storyteller_id || null,
          storyteller_name_telugu: group.telugu || null,
          storyteller_name_english: group.english || null,
          is_author: true,
          story_id: newStory.story_id,
        })
        .select().single();
      if (perfErr) throw perfErr;

      setStories((prev) => ({ ...prev, [newStory.story_id]: newStory }));
      setPerformances((prev) => [...prev, newPerf]);
      return newPerf;
    } catch (err) {
      console.error("Add Story failed:", err);
      window.alert(`Could not add story: ${err.message}`);
      return null;
    }
  }, [episodeId, setStories, setPerformances, db]);

  return { saveStatus, onSaveField, onGenerateReel, handleAddStory, registerReels };
}
